import { MastershopSyncStatus } from '../gamification/MastershopSyncStatus';
import React, { useState, useEffect } from 'react';
import { 
  X, 
  Store, 
  Plus, 
  Key, 
  CheckCircle2, 
  AlertCircle, 
  Clock, 
  Trash2, 
  Edit3, 
  RefreshCw, 
  Eye, 
  EyeOff, 
  ShieldCheck, 
  HelpCircle,
  TrendingUp,
  Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { mastershopHelpers, getCurrentYearMonth, formatMonthName } from '../../helpers/mastershopHelpers';
import { SellerStoreIntegration, SellerRevenueMonthly } from '../../types/sellerIntegration';
import { useToast } from '../../contexts/ToastContext';

interface ManageStoresModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  onStoresUpdated?: () => void;
}

export const ManageStoresModal: React.FC<ManageStoresModalProps> = ({
  isOpen,
  onClose,
  userId,
  onStoresUpdated,
}) => {
  const currentMonth = getCurrentYearMonth();
  const currentMonthLabel = formatMonthName(currentMonth);
  const { showToast } = useToast();

  const [stores, setStores] = useState<SellerStoreIntegration[]>([]);
  const [revenue, setRevenue] = useState<SellerRevenueMonthly | null>(null);
  const [loading, setLoading] = useState(true);

  // Estados para Adicionar Loja
  const [isAddingStore, setIsAddingStore] = useState(false);
  const [newStoreName, setNewStoreName] = useState('');
  const [newApiKey, setNewApiKey] = useState('');
  const [showNewKey, setShowNewKey] = useState(false);
  const [savingNew, setSavingNew] = useState(false);

  // Estados para Editar Loja
  const [editingStoreId, setEditingStoreId] = useState<string | null>(null);
  const [editStoreName, setEditStoreName] = useState('');
  const [editApiKey, setEditApiKey] = useState('');
  const [showEditKey, setShowEditKey] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  // Estados de Ações
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingStoreId, setSyncingStoreId] = useState<string | null>(null);
  const [deletingStoreId, setDeletingStoreId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && userId) {
      loadStoresData();
    }
  }, [isOpen, userId]);

  const loadStoresData = async () => {
    try {
      setLoading(true);
      const [storesRes, revRes] = await Promise.all([
        mastershopHelpers.getUserStores(userId),
        mastershopHelpers.getMonthlyRevenue(userId, currentMonth)
      ]);

      setStores(storesRes.data || []);
      if (revRes.data) {
        setRevenue(revRes.data);
      }
    } catch (err) {
      console.error('Erro ao carregar lojas:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewStore = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanKey = newApiKey.trim();
    if (!cleanKey) {
      showToast({
        type: 'warning',
        title: 'Chave Obrigatória',
        message: 'Por favor, insira a chave de API da Master Shop.',
      });
      return;
    }

    const defaultName = newStoreName.trim() || `Loja ${stores.length + 1}`;

    try {
      setSavingNew(true);
      const saveRes = await mastershopHelpers.saveApiKey(userId, cleanKey, defaultName);
      if (!saveRes.success) {
        throw new Error(saveRes.error || 'Falha ao salvar loja.');
      }

      showToast({
        type: 'success',
        title: 'Loja Conectada! 🎉',
        message: `Sincronizando faturamento de ${defaultName}...`,
      });

      // Limpar form
      setNewStoreName('');
      setNewApiKey('');
      setIsAddingStore(false);

      // Disparar sincronização consolidada
      await mastershopHelpers.syncRevenue(userId, currentMonth);

      await loadStoresData();
      onStoresUpdated?.();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Erro ao Conectar',
        message: err.message || 'Verifique a chave de API inserida.',
      });
    } finally {
      setSavingNew(false);
    }
  };

  const handleStartEdit = (store: SellerStoreIntegration) => {
    setEditingStoreId(store.id);
    setEditStoreName(store.store_name || '');
    setEditApiKey(store.api_key || '');
    setShowEditKey(false);
    setIsAddingStore(false);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingStoreId) return;

    const cleanKey = editApiKey.trim();
    if (!cleanKey) {
      showToast({
        type: 'warning',
        title: 'Chave Obrigatória',
        message: 'A chave de API não pode ficar vazia.',
      });
      return;
    }

    const name = editStoreName.trim() || 'Loja Master';

    try {
      setSavingEdit(true);
      const saveRes = await mastershopHelpers.saveApiKey(userId, cleanKey, name, editingStoreId);
      if (!saveRes.success) {
        throw new Error(saveRes.error || 'Falha ao atualizar loja.');
      }

      showToast({
        type: 'success',
        title: 'Loja Atualizada!',
        message: `Os dados de "${name}" foram salvos com sucesso.`,
      });

      setEditingStoreId(null);
      // Sincronizar após editar
      await mastershopHelpers.syncRevenue(userId, currentMonth);
      await loadStoresData();
      onStoresUpdated?.();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Erro ao Salvar',
        message: err.message || 'Não foi possível atualizar a loja.',
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteStore = async (store: SellerStoreIntegration) => {
    const title = store.store_name || 'esta loja';
    if (!window.confirm(`Tem certeza que deseja remover e desconectar "${title}"?`)) {
      return;
    }

    try {
      setDeletingStoreId(store.id);
      const res = await mastershopHelpers.deleteStore(store.id, userId);
      if (res.success) {
        showToast({
          type: 'info',
          title: 'Loja Removida',
          message: `A loja "${title}" foi desconectada.`,
        });
        await mastershopHelpers.syncRevenue(userId, currentMonth);
        await loadStoresData();
        onStoresUpdated?.();
      } else {
        showToast({
          type: 'error',
          title: 'Erro ao Remover',
          message: res.error || 'Falha ao excluir loja.',
        });
      }
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Erro',
        message: err.message,
      });
    } finally {
      setDeletingStoreId(null);
    }
  };

  const handleSyncSingleStore = async (store: SellerStoreIntegration) => {
    try {
      setSyncingStoreId(store.id);
      const res = await mastershopHelpers.syncRevenue(userId, currentMonth, store.api_key);
      if (res.success) {
        showToast({
          type: 'success',
          title: 'Sincronização solicitada',
          message: res.data?.queued === false ? 'Já houve uma solicitação recente. Aguarde cinco minutos para solicitar outra.' : 'Processamento em segundo plano. O total será atualizado ao concluir todas as lojas.',
        });
        await loadStoresData();
        onStoresUpdated?.();
      } else {
        showToast({
          type: 'error',
          title: 'Falha na Sincronização',
          message: res.error || 'Verifique a chave informada.',
        });
      }
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Erro',
        message: err.message,
      });
    } finally {
      setSyncingStoreId(null);
    }
  };

  const handleSyncAll = async () => {
    try {
      setSyncingAll(true);
      const res = await mastershopHelpers.syncRevenue(userId, currentMonth);
      if (res.success) {
        showToast({
          type: 'success',
          title: 'Sincronização solicitada',
          message: res.data?.queued === false ? 'Já houve uma solicitação recente. Aguarde cinco minutos para solicitar outra.' : 'Processamento em segundo plano. O total será atualizado ao concluir todas as lojas.',
        });
        await loadStoresData();
        onStoresUpdated?.();
      } else {
        showToast({
          type: 'error',
          title: 'Falha na Sincronização',
          message: res.error || 'Erro ao sincronizar.',
        });
      }
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Erro',
        message: err.message,
      });
    } finally {
      setSyncingAll(false);
    }
  };

  const formatBrl = (val: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 2,
    }).format(val || 0);
  };

  const formatCop = (val: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0,
    }).format(val || 0);
  };

  const maskApiKey = (key: string) => {
    if (!key) return '••••••••';
    if (key.length <= 8) return '••••••••';
    return `${key.slice(0, 7)}...${key.slice(-4)}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
      <div 
        className="bg-[#15171c] border border-gray-800 rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden relative"
        onClick={(e) => e.stopPropagation()}
      >
      <MastershopSyncStatus month={currentMonth} userId={userId} onUpdate={loadStoresData} />
        {/* Header do Modal */}
        <div className="p-6 border-b border-gray-800/80 flex items-center justify-between bg-[#1a1d24]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#0FFF55]/10 border border-[#0FFF55]/30 flex items-center justify-center text-[#0FFF55]">
              <Store className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-black text-white flex items-center gap-2">
                Gerenciar Lojas Master Shop
                <span className="bg-[#0FFF55]/20 text-[#0FFF55] text-xs px-2.5 py-0.5 rounded-full font-bold border border-[#0FFF55]/30">
                  {stores.length} {stores.length === 1 ? 'Loja' : 'Lojas'}
                </span>
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Adicione, altere ou exclua chaves de API da Master Shop.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white rounded-xl hover:bg-gray-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Conteúdo Rolável */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          {/* Ações no Topo */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button
              onClick={() => {
                setIsAddingStore(true);
                setEditingStoreId(null);
              }}
              size="sm"
              className="bg-gradient-to-r from-[#0FFF55] to-[#0dd149] hover:from-[#0dd149] hover:to-[#0FFF55] text-[#121316] font-black rounded-xl text-xs flex items-center gap-1.5 shadow-md shadow-[#0FFF55]/20"
            >
              <Plus className="w-4 h-4" />
              Adicionar Nova Chave / Loja
            </Button>

            {stores.length > 0 && (
              <Button
                onClick={handleSyncAll}
                disabled={syncingAll || loading}
                variant="outline"
                size="sm"
                className="border-gray-700 bg-gray-800/40 text-gray-200 hover:text-white hover:bg-gray-800 rounded-xl text-xs flex items-center gap-1.5"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${syncingAll ? 'animate-spin text-[#0FFF55]' : ''}`} />
                {syncingAll ? 'Sincronizando...' : 'Sincronizar Todas'}
              </Button>
            )}
          </div>

          {/* Formulário: Adicionar Nova Loja */}
          {isAddingStore && (
            <form onSubmit={handleAddNewStore} className="bg-[#191b22] p-5 rounded-2xl border border-[#0FFF55]/40 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200 shadow-xl">
              <div className="flex items-center justify-between pb-3 border-b border-gray-800">
                <span className="text-sm font-bold text-white flex items-center gap-2">
                  <Plus className="w-4 h-4 text-[#0FFF55]" />
                  Cadastrar Nova Loja
                </span>
                <button
                  type="button"
                  onClick={() => setIsAddingStore(false)}
                  className="text-xs text-gray-400 hover:text-white font-medium"
                >
                  Cancelar
                </button>
              </div>

              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-300 font-semibold block">Nome da Loja</Label>
                  <Input
                    type="text"
                    value={newStoreName}
                    onChange={(e) => setNewStoreName(e.target.value)}
                    placeholder={`Ex: Loja ${stores.length + 1}, Cosméticos, Moda`}
                    className="w-full bg-[#121418] border-gray-700 text-white rounded-xl text-xs h-10 px-3.5 focus:border-[#0FFF55] focus:ring-1 focus:ring-[#0FFF55]"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-300 font-semibold block">
                    Chave de API (ApiKey da Master Shop)
                  </Label>
                  <div className="relative">
                    <Input
                      type={showNewKey ? 'text' : 'password'}
                      value={newApiKey}
                      onChange={(e) => setNewApiKey(e.target.value)}
                      placeholder="ms_live_..."
                      className="w-full bg-[#121418] border-gray-700 text-white rounded-xl text-xs h-10 pl-3.5 pr-10 font-mono focus:border-[#0FFF55] focus:ring-1 focus:ring-[#0FFF55]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewKey(!showNewKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white p-1"
                    >
                      {showNewKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-gray-800">
                <Button
                  type="button"
                  onClick={() => setIsAddingStore(false)}
                  variant="ghost"
                  size="sm"
                  className="text-xs text-gray-400 hover:text-white h-9 px-3 rounded-xl"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={savingNew}
                  size="sm"
                  className="bg-[#0FFF55] hover:bg-[#0dd149] text-[#121316] font-black text-xs h-9 rounded-xl px-5 flex items-center gap-1.5 shadow-md shadow-[#0FFF55]/20"
                >
                  {savingNew ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                  {savingNew ? 'Conectando...' : 'Salvar e Conectar Loja'}
                </Button>
              </div>
            </form>
          )}

          {/* Formulário: Editar Loja Existente */}
          {editingStoreId && (
            <form onSubmit={handleSaveEdit} className="bg-[#191b22] p-5 rounded-2xl border border-[#0FFF55]/40 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200 shadow-xl">
              <div className="flex items-center justify-between pb-3 border-b border-gray-800">
                <span className="text-sm font-bold text-white flex items-center gap-2">
                  <Edit3 className="w-4 h-4 text-[#0FFF55]" />
                  Editar Dados / Trocar Chave da Loja
                </span>
                <button
                  type="button"
                  onClick={() => setEditingStoreId(null)}
                  className="text-xs text-gray-400 hover:text-white font-medium"
                >
                  Cancelar
                </button>
              </div>

              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-300 font-semibold block">Nome da Loja</Label>
                  <Input
                    type="text"
                    value={editStoreName}
                    onChange={(e) => setEditStoreName(e.target.value)}
                    placeholder="Nome da loja"
                    className="w-full bg-[#121418] border-gray-700 text-white rounded-xl text-xs h-10 px-3.5 focus:border-[#0FFF55] focus:ring-1 focus:ring-[#0FFF55]"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-300 font-semibold block">
                    Chave de API (ApiKey da Master Shop)
                  </Label>
                  <div className="relative">
                    <Input
                      type={showEditKey ? 'text' : 'password'}
                      value={editApiKey}
                      onChange={(e) => setEditApiKey(e.target.value)}
                      placeholder="Cole a nova ApiKey da Master Shop"
                      className="w-full bg-[#121418] border-gray-700 text-white rounded-xl text-xs h-10 pl-3.5 pr-10 font-mono focus:border-[#0FFF55] focus:ring-1 focus:ring-[#0FFF55]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowEditKey(!showEditKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white p-1"
                    >
                      {showEditKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-gray-800">
                <Button
                  type="button"
                  onClick={() => setEditingStoreId(null)}
                  variant="ghost"
                  size="sm"
                  className="text-xs text-gray-400 hover:text-white h-9 px-3 rounded-xl"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={savingEdit}
                  size="sm"
                  className="bg-[#0FFF55] hover:bg-[#0dd149] text-[#121316] font-black text-xs h-9 rounded-xl px-5 flex items-center gap-1.5 shadow-md shadow-[#0FFF55]/20"
                >
                  {savingEdit ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  {savingEdit ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
              </div>
            </form>
          )}

          {/* Lista de Lojas */}
          {loading ? (
            <div className="py-12 text-center text-gray-400 space-y-2">
              <RefreshCw className="w-6 h-6 animate-spin text-[#0FFF55] mx-auto" />
              <p className="text-xs">Carregando lojas...</p>
            </div>
          ) : stores.length === 0 ? (
            <div className="bg-[#191b22] border border-dashed border-gray-800 rounded-2xl p-8 text-center space-y-3">
              <Store className="w-10 h-10 text-gray-600 mx-auto" />
              <div>
                <h4 className="text-sm font-bold text-white">Nenhuma chave cadastrada</h4>
                <p className="text-xs text-gray-400 max-w-sm mx-auto mt-1">
                  Cadastre sua primeira chave de API da Master Shop para começar a computar seu faturamento na régua de metas.
                </p>
              </div>
              <Button
                onClick={() => setIsAddingStore(true)}
                size="sm"
                className="bg-[#0FFF55] text-[#121316] hover:bg-[#0dd149] font-black text-xs rounded-xl"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Cadastrar Chave Agora
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {stores.map((store, index) => {
                const isSyncing = syncingStoreId === store.id;
                const isDeleting = deletingStoreId === store.id;
                const hasError = store.last_sync_status === 'error';
                const isSuccess = store.last_sync_status === 'success';

                return (
                  <div
                    key={store.id}
                    className={`bg-[#191b22] p-4 rounded-2xl border transition-all ${
                      hasError ? 'border-red-500/40 bg-red-950/10' : 'border-gray-800 hover:border-gray-700'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-xl bg-[#0FFF55]/10 border border-[#0FFF55]/20 flex items-center justify-center text-[#0FFF55] font-black text-xs flex-shrink-0">
                          #{index + 1}
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-extrabold text-white text-sm truncate">
                            {store.store_name || `Loja ${index + 1}`}
                          </h4>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            {isSuccess ? (
                              <span className="text-[10px] font-bold text-green-400 flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Sincronizada
                              </span>
                            ) : hasError ? (
                              <span className="text-[10px] font-bold text-red-400 flex items-center gap-1" title={store.last_error_message}>
                                <AlertCircle className="w-3 h-3" /> Erro na Chave
                              </span>
                            ) : (
                              <span className="text-[10px] font-bold text-yellow-400 flex items-center gap-1">
                                <Clock className="w-3 h-3" /> Pendente
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Botões de Ação */}
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <Button
                          onClick={() => handleSyncSingleStore(store)}
                          disabled={isSyncing || syncingAll}
                          variant="outline"
                          size="sm"
                          className="border-gray-700 bg-gray-800/40 text-gray-200 hover:text-white rounded-lg h-8 px-2.5 text-xs"
                          title="Sincronizar loja"
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin text-[#0FFF55]' : ''}`} />
                        </Button>
                        <Button
                          onClick={() => handleStartEdit(store)}
                          variant="outline"
                          size="sm"
                          className="border-gray-700 bg-gray-800/40 text-gray-200 hover:text-[#0FFF55] rounded-lg h-8 px-2.5 text-xs"
                          title="Editar nome ou trocar chave"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          onClick={() => handleDeleteStore(store)}
                          disabled={isDeleting}
                          variant="outline"
                          size="sm"
                          className="border-red-500/30 bg-red-950/20 text-red-400 hover:bg-red-500/20 rounded-lg h-8 px-2.5 text-xs"
                          title="Excluir loja"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>

                    {/* Faturamento e Pedidos */}
                    <div className="grid grid-cols-2 gap-2 mt-3 bg-[#121418] p-2.5 rounded-xl text-xs border border-gray-800/80">
                      <div>
                        <span className="text-gray-400 text-[10px] uppercase font-bold block">Faturamento ({currentMonthLabel})</span>
                        <span className="font-black text-[#0FFF55] text-sm">{formatBrl(store.last_sync_revenue_brl || 0)}</span>
                        {Number(store.last_sync_revenue_cop || 0) > 0 && (
                          <span className="text-gray-500 text-[9px] block">{formatCop(store.last_sync_revenue_cop || 0)}</span>
                        )}
                      </div>
                      <div>
                        <span className="text-gray-400 text-[10px] uppercase font-bold block">Pedidos</span>
                        <span className="font-bold text-white text-sm">{store.last_sync_orders || 0} pedidos</span>
                      </div>
                    </div>

                    {/* Chave e Data */}
                    <div className="flex items-center justify-between text-[10px] text-gray-500 font-mono mt-2 pt-2 border-t border-gray-800">
                      <span className="flex items-center gap-1">
                        <Key className="w-3 h-3 text-gray-600" />
                        {maskApiKey(store.api_key)}
                      </span>
                      <span className="font-sans">
                        {store.last_sync_at ? new Date(store.last_sync_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : 'Nunca sincronizado'}
                      </span>
                    </div>

                    {hasError && store.last_error_message && (
                      <p className="text-[10px] text-red-400 bg-red-950/40 p-2 rounded-lg mt-2 border border-red-900/40 flex items-start gap-1">
                        <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                        <span>{store.last_error_message}</span>
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Guia Rápido */}
          <div className="bg-[#191b22] p-4 rounded-2xl border border-gray-800 space-y-2 text-xs text-gray-400">
            <div className="flex items-center gap-1.5 font-bold text-white">
              <HelpCircle className="w-4 h-4 text-[#0FFF55]" />
              Onde encontro minha ApiKey?
            </div>
            <p className="leading-relaxed text-[11px]">
              No painel da sua loja na <strong className="text-gray-300">Master Shop</strong>, acesse <strong className="text-gray-300">Configurações ➔ ApiKey</strong>, copie a chave e cole acima.
            </p>
          </div>
        </div>

        {/* Footer do Modal */}
        <div className="p-4 border-t border-gray-800/80 flex items-center justify-end bg-[#1a1d24]">
          <Button
            onClick={onClose}
            className="bg-gray-800 hover:bg-gray-700 text-white font-bold text-xs rounded-xl px-5 py-2"
          >
            Fechar
          </Button>
        </div>
      </div>
    </div>
  );
};
